import React, { useState, useMemo, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Badge } from "../components/ui/badge";
import {
  Hexagon,
  Calculator,
  Info,
  BookOpen,
  Lightbulb,
  Copy,
  Check,
  RotateCcw,
  Pentagon,
  Shapes,
  Ruler,
  Compass,
  TrendingUp,
  Eye,
  EyeOff,
  Download,
  Share2,
  Sparkles,
  Zap,
  Grid3x3
} from 'lucide-react';
import "@/App.css";

const PI = Math.PI;

// Comprehensive Polygon Diagram Component
const PolygonDiagram = ({ sides, sideLength, showLabels = true, showGrid = true, highlightFeatures = false }) => {
  if (sides < 3) {
    return (
      <svg viewBox="0 0 300 300" className="w-full h-full max-w-[360px] mx-auto">
        <text x="150" y="150" fontSize="14" fill="hsl(var(--muted-foreground))" textAnchor="middle">
          Enter at least 3 sides
        </text>
      </svg>
    );
  }
  
  const centerX = 150;
  const centerY = 150;
  
  const angleStep = (2 * PI) / sides;
  const radius = sideLength / (2 * Math.sin(PI / sides));
  const scale = Math.min(100, 100 / Math.max(1, radius / 100));
  const scaledRadius = radius * scale;
  
  const points = [];
  for (let i = 0; i < sides; i++) {
    const angle = i * angleStep - PI / 2;
    const x = centerX + scaledRadius * Math.cos(angle);
    const y = centerY + scaledRadius * Math.sin(angle);
    points.push({ x, y });
  }
  
  const pointsStr = points.map(p => `${p.x},${p.y}`).join(' ');
  
  // Calculate inradius for inscribed circle
  const inradius = sideLength / (2 * Math.tan(PI / sides));
  const scaledInradius = inradius * scale;
  
  return (
    <svg viewBox="0 0 300 300" className="w-full h-full max-w-[360px] mx-auto">
      <defs>
        <filter id="polygonShadow">
          <feDropShadow dx="0" dy="2" stdDeviation="4" floodOpacity="0.2"/>
        </filter>
        <linearGradient id="polygonGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="hsl(262, 83%, 58%)" stopOpacity="0.2" />
          <stop offset="100%" stopColor="hsl(271, 91%, 65%)" stopOpacity="0.05" />
        </linearGradient>
        <pattern id="gridPattern2" width="20" height="20" patternUnits="userSpaceOnUse">
          <path d="M 20 0 L 0 0 0 20" fill="none" stroke="hsl(var(--border))" strokeWidth="0.5" opacity="0.3"/>
        </pattern>
      </defs>
      
      {/* Background */}
      {showGrid && <rect width="300" height="300" fill="url(#gridPattern2)" />}
      
      {/* Center point */}
      <circle cx={centerX} cy={centerY} r="4" fill="hsl(var(--foreground))" stroke="white" strokeWidth="2" />
      
      {/* Circumscribed circle (optional) */}
      {highlightFeatures && (
        <circle
          cx={centerX}
          cy={centerY}
          r={scaledRadius}
          fill="none"
          stroke="hsl(199, 89%, 48%)"
          strokeWidth="1.5"
          strokeDasharray="5 3"
          opacity="0.5"
        />
      )}
      
      {/* Inscribed circle (optional) */}
      {highlightFeatures && (
        <circle
          cx={centerX}
          cy={centerY}
          r={scaledInradius}
          fill="hsl(var(--primary) / 0.05)"
          stroke="hsl(184, 77%, 34%)"
          strokeWidth="1.5"
          strokeDasharray="4 2"
          opacity="0.7"
        />
      )}
      
      {/* Radii to vertices */}
      {points.map((point, i) => (
        <line
          key={`radius-${i}`}
          x1={centerX}
          y1={centerY}
          x2={point.x}
          y2={point.y}
          stroke="hsl(var(--muted-foreground))"
          strokeWidth="1"
          strokeDasharray="3 2"
          opacity="0.4"
        />
      ))}
      
      {/* Polygon */}
      <polygon
        points={pointsStr}
        fill="url(#polygonGradient)"
        stroke="hsl(262, 83%, 58%)"
        strokeWidth="3.5"
        strokeLinejoin="round"
        filter="url(#polygonShadow)"
      />
      
      {/* Vertices */}
      {points.map((point, i) => (
        <circle
          key={`vertex-${i}`}
          cx={point.x}
          cy={point.y}
          r="5"
          fill="#8b5cf6"
          stroke="white"
          strokeWidth="2"
        />
      ))}
      
      {/* Side length label */}
      {showLabels && points.length >= 2 && (
        <>
          <line
            x1={points[0].x}
            y1={points[0].y}
            x2={points[1].x}
            y2={points[1].y}
            stroke="#ef4444"
            strokeWidth="3"
          />
          <text
            x={(points[0].x + points[1].x) / 2}
            y={(points[0].y + points[1].y) / 2 - 12}
            fontSize="13"
            fill="#ef4444"
            fontWeight="700"
            textAnchor="middle"
          >
            s={sideLength.toFixed(2)}
          </text>
        </>
      )}
      
      {/* Polygon name */}
      <text
        x={centerX}
        y="285"
        fontSize="14"
        fill="hsl(var(--foreground))"
        fontWeight="700"
        textAnchor="middle"
      >
        {getPolygonName(sides)}
      </text>
      
      {/* Radii labels */}
      {showLabels && highlightFeatures && (
        <>
          <text
            x={centerX + scaledRadius + 15}
            y={centerY + 5}
            fontSize="11"
            fill="hsl(199, 89%, 48%)"
            fontWeight="600"
          >
            R={radius.toFixed(2)}
          </text>
          <text
            x={centerX}
            y={centerY + scaledInradius + 15}
            fontSize="11"
            fill="hsl(184, 77%, 34%)"
            fontWeight="600"
            textAnchor="middle"
          >
            r={inradius.toFixed(2)}
          </text>
        </>
      )}
    </svg>
  );
};

const getPolygonName = (sides) => {
  const names = {
    3: 'Triangle (Trigon)',
    4: 'Quadrilateral (Tetragon)',
    5: 'Pentagon',
    6: 'Hexagon',
    7: 'Heptagon (Septagon)',
    8: 'Octagon',
    9: 'Nonagon (Enneagon)',
    10: 'Decagon',
    11: 'Hendecagon (Undecagon)',
    12: 'Dodecagon',
    13: 'Triskaidecagon',
    14: 'Tetrakaidecagon',
    15: 'Pentadecagon',
    16: 'Hexadecagon',
    17: 'Heptadecagon',
    18: 'Octadecagon',
    19: 'Enneadecagon',
    20: 'Icosagon'
  };
  return names[sides] || `${sides}-gon`;
};

// Copy Button
const CopyButton = ({ text, label }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleCopy} title={`Copy ${label}`}>
      <AnimatePresence mode="wait">
        {copied ? (
          <motion.div key="check" initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}>
            <Check className="w-3.5 h-3.5 text-green-500" />
          </motion.div>
        ) : (
          <motion.div key="copy" initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}>
            <Copy className="w-3.5 h-3.5" />
          </motion.div>
        )}
      </AnimatePresence>
    </Button>
  );
};

// Property Card
const PropertyCard = ({ label, value, color, icon: Icon, formula }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    className={`rounded-xl p-4 ${color} border`}
  >
    <div className="flex items-start justify-between mb-2">
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 opacity-70" />}
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
      </div>
      <CopyButton text={value} label={label} />
    </div>
    <div className="font-mono text-lg font-bold text-foreground mb-1">
      {value}
    </div>
    {formula && (
      <div className="text-xs text-muted-foreground font-medium mt-2 font-mono bg-background/50 px-2 py-1 rounded">
        {formula}
      </div>
    )}
  </motion.div>
);

function PolygonCalculator() {
  const [sides, setSides] = useState(6);
  const [sideLength, setSideLength] = useState(5);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showFormulas, setShowFormulas] = useState(true);
  const [highlightCircles, setHighlightCircles] = useState(false);
  
  // Calculate all polygon properties
  const properties = useMemo(() => {
    if (sides < 3) return null;
    
    const n = sides;
    const s = sideLength;
    
    // Angles
    const interiorAngle = ((n - 2) * 180) / n;
    const exteriorAngle = 360 / n;
    const sumInteriorAngles = (n - 2) * 180;
    const centralAngle = 360 / n;
    const centralAngleRad = (2 * PI) / n;
    
    // Perimeter and Area
    const perimeter = n * s;
    const area = (n * s * s) / (4 * Math.tan(PI / n));
    
    // Radii
    const circumradius = s / (2 * Math.sin(PI / n));
    const inradius = s / (2 * Math.tan(PI / n));
    const apothem = inradius; // Same as inradius
    
    // Diagonals
    const numDiagonals = (n * (n - 3)) / 2;
    const shortestDiagonal = n > 3 ? s * Math.sqrt(1 + (1 / Math.tan(PI / n)) ** 2) : 0;
    const longestDiagonal = n % 2 === 0 ? 2 * circumradius : 0;
    
    // Advanced properties
    const triangleArea = area / n; // Area of one central triangle
    const sideAngleToCenter = (180 - centralAngle) / 2;
    
    // Symmetry
    const rotationalSymmetry = n;
    const reflectionalSymmetry = n;
    
    return {
      sides: n,
      sideLength: s,
      interiorAngle,
      exteriorAngle,
      sumInteriorAngles,
      centralAngle,
      centralAngleRad,
      perimeter,
      area,
      circumradius,
      inradius,
      apothem,
      numDiagonals,
      shortestDiagonal,
      longestDiagonal,
      triangleArea,
      sideAngleToCenter,
      rotationalSymmetry,
      reflectionalSymmetry
    };
  }, [sides, sideLength]);
  
  const handleReset = useCallback(() => {
    setSides(6);
    setSideLength(5);
    setShowAdvanced(false);
    setHighlightCircles(false);
  }, []);
  
  const handleExport = () => {
    const data = {
      ...properties,
      polygonName: getPolygonName(sides),
      timestamp: new Date().toISOString()
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `polygon-${sides}-sides-${Date.now()}.json`;
    a.click();
  };
  
  if (!properties) return null;
  
  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      <Helmet>
        <title>Polygon Properties Calculator - Regular Polygons</title>
        <meta name="description" content="Professional polygon calculator for regular polygons. Calculate interior angles, exterior angles, area, perimeter, diagonals, circumradius, inradius, and more." />
      </Helmet>
      
      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg">
              <Hexagon className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-foreground">Polygon Properties Calculator</h1>
              <p className="text-muted-foreground mt-1">Calculate all properties of regular polygons</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleExport} className="gap-2">
              <Download className="w-4 h-4" />
              Export
            </Button>
            <Button variant="outline" size="sm" className="gap-2">
              <Share2 className="w-4 h-4" />
              Share
            </Button>
          </div>
        </div>
        
        {/* Quick Stats Bar */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-2 sm:grid-cols-4 gap-3"
        >
          <Card className="p-3 bg-purple-500/5 border-purple-500/20">
            <div className="text-xs text-muted-foreground mb-1">Shape</div>
            <div className="text-lg font-bold text-purple-600">{getPolygonName(sides)}</div>
          </Card>
          <Card className="p-3 bg-pink-500/5 border-pink-500/20">
            <div className="text-xs text-muted-foreground mb-1">Area</div>
            <div className="text-lg font-bold text-pink-600">{properties.area.toFixed(2)}</div>
          </Card>
          <Card className="p-3 bg-violet-500/5 border-violet-500/20">
            <div className="text-xs text-muted-foreground mb-1">Perimeter</div>
            <div className="text-lg font-bold text-violet-600">{properties.perimeter.toFixed(2)}</div>
          </Card>
          <Card className="p-3 bg-fuchsia-500/5 border-fuchsia-500/20">
            <div className="text-xs text-muted-foreground mb-1">Interior Angle</div>
            <div className="text-lg font-bold text-fuchsia-600">{properties.interiorAngle.toFixed(2)}°</div>
          </Card>
        </motion.div>
      </motion.div>
      
      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left Column - Input Panel */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="lg:sticky lg:top-24 lg:self-start space-y-6"
        >
          {/* Input Controls */}
          <Card className="rounded-2xl p-6 border-2 shadow-lg">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Calculator className="w-5 h-5 text-purple-600" />
                <span className="font-semibold text-foreground text-lg">Input Values</span>
              </div>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-9 w-9 hover:bg-destructive/10 hover:text-destructive" 
                onClick={handleReset}
                title="Reset All"
              >
                <RotateCcw className="w-4 h-4" />
              </Button>
            </div>
            
            {/* Input Fields */}
            <div className="space-y-5">
              <div>
                <Label className="text-sm mb-2 font-medium flex items-center gap-2">
                  <Shapes className="w-4 h-4" />
                  Number of Sides
                </Label>
                <Input
                  type="number"
                  value={sides}
                  onChange={(e) => setSides(Math.max(3, Math.min(100, parseInt(e.target.value) || 3)))}
                  className="h-12 text-lg font-mono"
                  min="3"
                  max="100"
                  step="1"
                />
                <input
                  type="range"
                  min="3"
                  max="20"
                  step="1"
                  value={Math.min(20, sides)}
                  onChange={(e) => setSides(parseInt(e.target.value))}
                  className="w-full h-2 bg-secondary rounded-lg appearance-none cursor-pointer accent-purple-600 mt-4"
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-1.5">
                  <span>3 sides</span>
                  <span className="font-semibold text-purple-600">{sides} sides</span>
                  <span>20 sides</span>
                </div>
              </div>
              
              <div>
                <Label className="text-sm mb-2 font-medium flex items-center gap-2">
                  <Ruler className="w-4 h-4" />
                  Side Length
                </Label>
                <Input
                  type="number"
                  value={sideLength}
                  onChange={(e) => setSideLength(Math.max(0.1, parseFloat(e.target.value) || 0))}
                  className="h-12 text-lg font-mono"
                  min="0.1"
                  step="0.1"
                />
                <div className="text-xs text-muted-foreground mt-1.5">Length of each side</div>
              </div>
            </div>
            
            {/* Quick Shape Presets */}
            <div className="mt-6">
              <Label className="text-xs text-muted-foreground mb-3 block font-medium">Quick Shape Selection</Label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { n: 3, name: 'Triangle', icon: '△' },
                  { n: 4, name: 'Square', icon: '□' },
                  { n: 5, name: 'Pentagon', icon: '⬠' },
                  { n: 6, name: 'Hexagon', icon: '⬡' },
                  { n: 8, name: 'Octagon', icon: '⯃' },
                  { n: 12, name: 'Dodecagon', icon: '⬢' }
                ].map((shape) => (
                  <Button
                    key={shape.n}
                    variant="outline"
                    size="sm"
                    className={`text-xs rounded-lg transition-all flex flex-col h-auto py-2 ${
                      sides === shape.n 
                        ? 'bg-purple-600 text-white border-purple-600 hover:bg-purple-700 hover:text-white' 
                        : 'hover:bg-purple-50 hover:border-purple-300'
                    }`}
                    onClick={() => setSides(shape.n)}
                  >
                    <span className="text-xl mb-1">{shape.icon}</span>
                    <span>{shape.name}</span>
                  </Button>
                ))}
              </div>
            </div>
            
            {/* Current Shape Info */}
            <div className="mt-6 p-5 bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-xl border border-purple-500/20">
              <div className="text-center">
                <span className="text-sm text-muted-foreground block mb-1">Current Shape</span>
                <span className="text-2xl font-bold text-purple-600 block">{getPolygonName(sides)}</span>
                <span className="text-sm text-muted-foreground block mt-1">{sides} sides • {properties.numDiagonals} diagonals</span>
              </div>
            </div>
          </Card>
        </motion.div>
        
        {/* Center/Right Column - Visualization & Results */}
        <div className="lg:col-span-2 space-y-6">
          {/* Visualization */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="rounded-2xl p-6 border-2 shadow-lg">
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-semibold text-foreground flex items-center gap-2 text-lg">
                  <Pentagon className="w-5 h-5 text-purple-600" />
                  Interactive Polygon Diagram
                  <Badge variant="secondary" className="ml-2 text-xs">Real-time</Badge>
                </h2>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setHighlightCircles(!highlightCircles)}
                    className="gap-2"
                  >
                    <Compass className="w-3 h-3" />
                    <span className="hidden sm:inline">{highlightCircles ? 'Hide' : 'Show'} Circles</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowFormulas(!showFormulas)}
                    className="gap-2"
                  >
                    {showFormulas ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                    <span className="hidden sm:inline">Formulas</span>
                  </Button>
                </div>
              </div>
              
              <div className="rounded-xl p-6 bg-gradient-to-br from-purple-500/5 to-pink-500/5 border-2 border-purple-500/20">
                <PolygonDiagram 
                  sides={sides} 
                  sideLength={sideLength} 
                  showLabels={true}
                  showGrid={true}
                  highlightFeatures={highlightCircles}
                />
              </div>
              
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="text-center p-3 rounded-lg bg-background border">
                  <div className="text-xs text-muted-foreground mb-1">Sides</div>
                  <div className="font-bold text-purple-600">{sides}</div>
                </div>
                <div className="text-center p-3 rounded-lg bg-background border">
                  <div className="text-xs text-muted-foreground mb-1">Diagonals</div>
                  <div className="font-bold text-pink-600">{properties.numDiagonals}</div>
                </div>
                <div className="text-center p-3 rounded-lg bg-background border">
                  <div className="text-xs text-muted-foreground mb-1">Vertices</div>
                  <div className="font-bold text-violet-600">{sides}</div>
                </div>
                <div className="text-center p-3 rounded-lg bg-background border">
                  <div className="text-xs text-muted-foreground mb-1">Symmetry</div>
                  <div className="font-bold text-fuchsia-600">{sides}-fold</div>
                </div>
              </div>
            </Card>
          </motion.div>
          
          {/* Angles Properties */}
          <Card className="rounded-2xl p-6 border-2 shadow-lg bg-gradient-to-br from-orange-500/5 to-amber-500/5">
            <h2 className="font-semibold text-foreground flex items-center gap-2 mb-5 text-lg">
              <TrendingUp className="w-5 h-5 text-orange-600" />
              Angle Properties
              <Sparkles className="w-4 h-4 text-yellow-500 ml-auto" />
            </h2>
            
            <div className="grid sm:grid-cols-2 gap-4">
              <PropertyCard
                label="Interior Angle"
                value={`${properties.interiorAngle.toFixed(4)}°`}
                color="bg-orange-500/10 border-orange-500/20"
                formula={showFormulas ? "θ = (n-2)×180°/n" : null}
              />
              <PropertyCard
                label="Exterior Angle"
                value={`${properties.exteriorAngle.toFixed(4)}°`}
                color="bg-amber-500/10 border-amber-500/20"
                formula={showFormulas ? "360°/n" : null}
              />
              <PropertyCard
                label="Sum of Interior Angles"
                value={`${properties.sumInteriorAngles.toFixed(0)}°`}
                color="bg-yellow-500/10 border-yellow-500/20"
                formula={showFormulas ? "(n-2)×180°" : null}
              />
              <PropertyCard
                label="Central Angle"
                value={`${properties.centralAngle.toFixed(4)}°`}
                color="bg-orange-600/10 border-orange-600/20"
                formula={showFormulas ? "360°/n" : null}
              />
              <PropertyCard
                label="Central Angle (radians)"
                value={properties.centralAngleRad.toFixed(6)}
                color="bg-red-500/10 border-red-500/20"
                formula={showFormulas ? "2π/n" : null}
              />
              <PropertyCard
                label="Side Angle to Center"
                value={`${properties.sideAngleToCenter.toFixed(4)}°`}
                color="bg-rose-500/10 border-rose-500/20"
                formula={showFormulas ? "(180°-central)/2" : null}
              />
            </div>
          </Card>
          
          {/* Measurements */}
          <Card className="rounded-2xl p-6 border-2 shadow-lg bg-gradient-to-br from-blue-500/5 to-cyan-500/5">
            <h2 className="font-semibold text-foreground flex items-center gap-2 mb-5 text-lg">
              <Ruler className="w-5 h-5 text-blue-600" />
              Linear Measurements
            </h2>
            
            <div className="grid sm:grid-cols-2 gap-4">
              <PropertyCard
                label="Side Length"
                value={properties.sideLength.toFixed(6)}
                color="bg-blue-500/10 border-blue-500/20"
                formula={showFormulas ? "s (given)" : null}
              />
              <PropertyCard
                label="Perimeter"
                value={properties.perimeter.toFixed(6)}
                color="bg-cyan-500/10 border-cyan-500/20"
                formula={showFormulas ? "P = n×s" : null}
              />
              <PropertyCard
                label="Circumradius (R)"
                value={properties.circumradius.toFixed(6)}
                color="bg-sky-500/10 border-sky-500/20"
                formula={showFormulas ? "R = s/(2sin(π/n))" : null}
              />
              <PropertyCard
                label="Inradius / Apothem (r)"
                value={properties.inradius.toFixed(6)}
                color="bg-teal-500/10 border-teal-500/20"
                formula={showFormulas ? "r = s/(2tan(π/n))" : null}
              />
            </div>
          </Card>
          
          {/* Area */}
          <Card className="rounded-2xl p-6 border-2 shadow-lg bg-gradient-to-br from-purple-500/5 to-pink-500/5">
            <h2 className="font-semibold text-foreground flex items-center gap-2 mb-5 text-lg">
              <Grid3x3 className="w-5 h-5 text-purple-600" />
              Area Calculations
            </h2>
            
            <div className="grid sm:grid-cols-2 gap-4">
              <PropertyCard
                label="Total Area"
                value={properties.area.toFixed(6)}
                color="bg-purple-500/10 border-purple-500/20"
                formula={showFormulas ? "A = ns²/(4tan(π/n))" : null}
              />
              <PropertyCard
                label="Area (using apothem)"
                value={properties.area.toFixed(6)}
                color="bg-pink-500/10 border-pink-500/20"
                formula={showFormulas ? "A = (P×r)/2" : null}
              />
              <PropertyCard
                label="Central Triangle Area"
                value={properties.triangleArea.toFixed(6)}
                color="bg-violet-500/10 border-violet-500/20"
                formula={showFormulas ? "A/n" : null}
              />
              <PropertyCard
                label="Area (using circumradius)"
                value={properties.area.toFixed(6)}
                color="bg-fuchsia-500/10 border-fuchsia-500/20"
                formula={showFormulas ? "A = (nR²sin(2π/n))/2" : null}
              />
            </div>
          </Card>
          
          {/* Diagonals - Advanced */}
          <div>
            <Button
              variant="outline"
              className="w-full mb-4 gap-2"
              onClick={() => setShowAdvanced(!showAdvanced)}
            >
              {showAdvanced ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              {showAdvanced ? 'Hide' : 'Show'} Advanced Properties (Diagonals & More)
              <Zap className="w-4 h-4 ml-auto text-yellow-500" />
            </Button>
            
            <AnimatePresence>
              {showAdvanced && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <Card className="rounded-2xl p-6 border-2 shadow-lg bg-gradient-to-br from-green-500/5 to-emerald-500/5">
                    <h2 className="font-semibold text-foreground flex items-center gap-2 mb-5 text-lg">
                      <Sparkles className="w-5 h-5 text-green-600" />
                      Diagonal Properties & Symmetry
                    </h2>
                    
                    <div className="space-y-6">
                      {/* Diagonals */}
                      <div>
                        <h3 className="text-sm font-semibold text-muted-foreground mb-3">Diagonal Information</h3>
                        <div className="grid sm:grid-cols-2 gap-4">
                          <PropertyCard
                            label="Number of Diagonals"
                            value={properties.numDiagonals.toString()}
                            color="bg-green-500/10 border-green-500/20"
                            formula={showFormulas ? "D = n(n-3)/2" : null}
                          />
                          {properties.shortestDiagonal > 0 && (
                            <PropertyCard
                              label="Shortest Diagonal"
                              value={properties.shortestDiagonal.toFixed(6)}
                              color="bg-emerald-500/10 border-emerald-500/20"
                              formula={showFormulas ? "For adjacent vertices" : null}
                            />
                          )}
                          {properties.longestDiagonal > 0 && (
                            <PropertyCard
                              label="Longest Diagonal"
                              value={properties.longestDiagonal.toFixed(6)}
                              color="bg-teal-500/10 border-teal-500/20"
                              formula={showFormulas ? "2R (even-sided)" : null}
                            />
                          )}
                        </div>
                      </div>
                      
                      {/* Symmetry */}
                      <div className="pt-4 border-t border-border">
                        <h3 className="text-sm font-semibold text-muted-foreground mb-3">Symmetry Properties</h3>
                        <div className="grid sm:grid-cols-2 gap-4">
                          <PropertyCard
                            label="Rotational Symmetry"
                            value={`${properties.rotationalSymmetry}-fold`}
                            color="bg-lime-500/10 border-lime-500/20"
                            formula={showFormulas ? "n rotations" : null}
                          />
                          <PropertyCard
                            label="Reflectional Symmetry"
                            value={`${properties.reflectionalSymmetry} axes`}
                            color="bg-green-600/10 border-green-600/20"
                            formula={showFormulas ? "n reflection axes" : null}
                          />
                        </div>
                      </div>
                      
                      {/* Relationship between radii */}
                      <div className="pt-4 border-t border-border">
                        <h3 className="text-sm font-semibold text-muted-foreground mb-3">Radius Relationships</h3>
                        <div className="p-4 bg-background/60 rounded-lg border">
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between items-center">
                              <span className="text-muted-foreground">Ratio R/r:</span>
                              <span className="font-mono font-bold">{(properties.circumradius / properties.inradius).toFixed(6)}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-muted-foreground">Ratio R/s:</span>
                              <span className="font-mono font-bold">{(properties.circumradius / properties.sideLength).toFixed(6)}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-muted-foreground">Ratio r/s:</span>
                              <span className="font-mono font-bold">{(properties.inradius / properties.sideLength).toFixed(6)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          
          {/* Complete Formulas Reference */}
          <Card className="rounded-2xl p-6 border-2 shadow-lg">
            <h2 className="text-xl font-bold text-foreground mb-5 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-purple-600" />
              Complete Polygon Formulas Reference
            </h2>
            
            <div className="space-y-5">
              {/* Basic Formulas */}
              <div className="p-5 rounded-xl bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20">
                <h3 className="font-bold text-foreground mb-3">For Regular Polygons with n sides and side length s:</h3>
                <div className="space-y-3 text-sm">
                  <div className="p-3 bg-background/60 rounded-lg">
                    <div className="font-semibold mb-1 text-purple-600">Interior Angle</div>
                    <code className="font-mono text-xs">θ = (n - 2) × 180° / n</code>
                    <p className="text-xs text-muted-foreground mt-1">Each interior angle of the polygon</p>
                  </div>
                  <div className="p-3 bg-background/60 rounded-lg">
                    <div className="font-semibold mb-1 text-pink-600">Exterior Angle</div>
                    <code className="font-mono text-xs">360° / n</code>
                    <p className="text-xs text-muted-foreground mt-1">Each exterior angle (sum always equals 360°)</p>
                  </div>
                  <div className="p-3 bg-background/60 rounded-lg">
                    <div className="font-semibold mb-1 text-violet-600">Perimeter</div>
                    <code className="font-mono text-xs">P = n × s</code>
                    <p className="text-xs text-muted-foreground mt-1">Total distance around the polygon</p>
                  </div>
                  <div className="p-3 bg-background/60 rounded-lg">
                    <div className="font-semibold mb-1 text-fuchsia-600">Area</div>
                    <code className="font-mono text-xs">A = (n × s²) / (4 × tan(π/n))</code>
                    <p className="text-xs text-muted-foreground mt-1">Total area enclosed by the polygon</p>
                  </div>
                  <div className="p-3 bg-background/60 rounded-lg">
                    <div className="font-semibold mb-1 text-purple-600">Area (alternative)</div>
                    <code className="font-mono text-xs">A = (1/2) × P × r = (1/2) × n × s × r</code>
                    <p className="text-xs text-muted-foreground mt-1">Using perimeter and apothem</p>
                  </div>
                </div>
              </div>
              
              {/* Radii Formulas */}
              <div className="p-5 rounded-xl bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/20">
                <h3 className="font-bold text-blue-600 mb-3">Circumradius & Inradius</h3>
                <div className="space-y-3 text-sm">
                  <div className="p-3 bg-background/60 rounded-lg">
                    <div className="font-semibold mb-1 text-blue-600">Circumradius (R)</div>
                    <code className="font-mono text-xs">R = s / (2 × sin(π/n))</code>
                    <p className="text-xs text-muted-foreground mt-1">Radius of circle passing through all vertices</p>
                  </div>
                  <div className="p-3 bg-background/60 rounded-lg">
                    <div className="font-semibold mb-1 text-cyan-600">Inradius / Apothem (r)</div>
                    <code className="font-mono text-xs">r = s / (2 × tan(π/n))</code>
                    <p className="text-xs text-muted-foreground mt-1">Radius of inscribed circle (perpendicular distance to sides)</p>
                  </div>
                  <div className="p-3 bg-background/60 rounded-lg">
                    <div className="font-semibold mb-1 text-teal-600">Relationship</div>
                    <code className="font-mono text-xs">r = R × cos(π/n)</code>
                    <p className="text-xs text-muted-foreground mt-1">Connection between circumradius and inradius</p>
                  </div>
                </div>
              </div>
              
              {/* Diagonals */}
              <div className="p-5 rounded-xl bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/20">
                <h3 className="font-bold text-green-600 mb-3">Diagonals</h3>
                <div className="space-y-3 text-sm">
                  <div className="p-3 bg-background/60 rounded-lg">
                    <div className="font-semibold mb-1 text-green-600">Number of Diagonals</div>
                    <code className="font-mono text-xs">D = n(n - 3) / 2</code>
                    <p className="text-xs text-muted-foreground mt-1">Total number of line segments connecting non-adjacent vertices</p>
                  </div>
                  <div className="p-3 bg-background/60 rounded-lg">
                    <div className="font-semibold mb-1 text-emerald-600">Diagonal Length (k vertices apart)</div>
                    <code className="font-mono text-xs">d = 2R × sin(kπ/n)</code>
                    <p className="text-xs text-muted-foreground mt-1">Where k is the number of vertices between endpoints</p>
                  </div>
                </div>
              </div>
              
              {/* Special Cases */}
              <div className="p-5 rounded-xl bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20">
                <h3 className="font-bold text-amber-600 mb-3">Special Polygon Cases</h3>
                <div className="space-y-3 text-sm">
                  <div className="p-3 bg-background/60 rounded-lg">
                    <div className="font-semibold mb-1 text-amber-600">Equilateral Triangle (n=3)</div>
                    <code className="font-mono text-xs block mb-1">Area = (√3/4)s²</code>
                    <code className="font-mono text-xs block">Height = (√3/2)s</code>
                  </div>
                  <div className="p-3 bg-background/60 rounded-lg">
                    <div className="font-semibold mb-1 text-orange-600">Square (n=4)</div>
                    <code className="font-mono text-xs block mb-1">Area = s²</code>
                    <code className="font-mono text-xs block">Diagonal = s√2</code>
                  </div>
                  <div className="p-3 bg-background/60 rounded-lg">
                    <div className="font-semibold mb-1 text-red-600">Regular Hexagon (n=6)</div>
                    <code className="font-mono text-xs block mb-1">Area = (3√3/2)s²</code>
                    <code className="font-mono text-xs block">Can tessellate a plane</code>
                  </div>
                </div>
              </div>
            </div>
          </Card>
          
          {/* Comprehensive FAQ */}
          <Card className="rounded-2xl p-6 border-2 shadow-lg">
            <h2 className="text-xl font-bold text-foreground mb-5 flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-yellow-500" />
              Frequently Asked Questions
            </h2>
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="faq-1" className="border-b border-border">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium text-foreground">What is a regular polygon?</span>
                </AccordionTrigger>
                <AccordionContent>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    A regular polygon is a polygon where all sides have equal length and all interior angles are equal. These special polygons have perfect symmetry and many elegant mathematical properties.
                  </p>
                  <div className="bg-muted/30 p-4 rounded-lg">
                    <p className="text-sm font-semibold mb-2">Examples of Regular Polygons:</p>
                    <ul className="space-y-1 text-sm text-muted-foreground">
                      <li>• Equilateral Triangle (3 sides)</li>
                      <li>• Square (4 sides)</li>
                      <li>• Regular Pentagon (5 sides)</li>
                      <li>• Regular Hexagon (6 sides)</li>
                    </ul>
                  </div>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="faq-2" className="border-b border-border">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium text-foreground">Why do exterior angles always sum to 360°?</span>
                </AccordionTrigger>
                <AccordionContent>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    Imagine walking around the perimeter of any polygon. At each vertex, you need to turn by the exterior angle. By the time you complete the full circuit and return to your starting point, you've made one complete rotation - exactly 360°!
                  </p>
                  <div className="bg-blue-500/10 p-4 rounded-lg border border-blue-500/20">
                    <p className="text-sm font-semibold mb-2">This works for ANY polygon:</p>
                    <ul className="space-y-1 text-sm text-muted-foreground">
                      <li>• Triangle: 3 × 120° = 360°</li>
                      <li>• Square: 4 × 90° = 360°</li>
                      <li>• Pentagon: 5 × 72° = 360°</li>
                      <li>• Hexagon: 6 × 60° = 360°</li>
                    </ul>
                  </div>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="faq-3" className="border-b border-border">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium text-foreground">What are circumradius and inradius?</span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-4">
                    <div className="bg-purple-500/10 p-4 rounded-lg border border-purple-500/20">
                      <h4 className="font-semibold text-sm mb-2 text-purple-600">Circumradius (R)</h4>
                      <p className="text-sm text-muted-foreground">
                        The radius of the circle that passes through all vertices of the polygon (circumscribed circle). It's the distance from the center to any vertex.
                      </p>
                    </div>
                    <div className="bg-teal-500/10 p-4 rounded-lg border border-teal-500/20">
                      <h4 className="font-semibold text-sm mb-2 text-teal-600">Inradius / Apothem (r)</h4>
                      <p className="text-sm text-muted-foreground">
                        The radius of the circle that touches all sides from the inside (inscribed circle). It's the perpendicular distance from the center to any side, also called the apothem.
                      </p>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      <strong>Key relationship:</strong> r = R × cos(π/n), where n is the number of sides.
                    </p>
                  </div>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="faq-4" className="border-b border-border">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium text-foreground">How do I calculate the number of diagonals?</span>
                </AccordionTrigger>
                <AccordionContent>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    The formula D = n(n-3)/2 comes from a simple counting principle:
                  </p>
                  <div className="bg-muted/30 p-4 rounded-lg space-y-2 text-sm">
                    <p><strong>Step 1:</strong> From each vertex, you can draw lines to (n-1) other vertices</p>
                    <p><strong>Step 2:</strong> But 2 of those are sides, not diagonals, leaving (n-3) diagonals per vertex</p>
                    <p><strong>Step 3:</strong> Multiply by n vertices: n(n-3)</p>
                    <p><strong>Step 4:</strong> Divide by 2 since each diagonal connects two vertices (counted twice)</p>
                  </div>
                  <div className="mt-3 bg-green-500/10 p-3 rounded border border-green-500/20">
                    <p className="text-sm"><strong>Examples:</strong></p>
                    <p className="text-xs text-muted-foreground">Pentagon: 5(5-3)/2 = 5 diagonals</p>
                    <p className="text-xs text-muted-foreground">Hexagon: 6(6-3)/2 = 9 diagonals</p>
                  </div>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="faq-5" className="border-b border-border">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium text-foreground">Which polygons can tessellate a plane?</span>
                </AccordionTrigger>
                <AccordionContent>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    A polygon tessellates (tiles) a plane if copies of it can fit together perfectly with no gaps or overlaps. For regular polygons, only three can do this:
                  </p>
                  <div className="space-y-2">
                    <div className="bg-background p-3 rounded-lg border">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg">△</span>
                        <span className="font-semibold text-sm">Equilateral Triangles</span>
                      </div>
                      <p className="text-xs text-muted-foreground">6 triangles meet at each vertex (6 × 60° = 360°)</p>
                    </div>
                    <div className="bg-background p-3 rounded-lg border">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg">□</span>
                        <span className="font-semibold text-sm">Squares</span>
                      </div>
                      <p className="text-xs text-muted-foreground">4 squares meet at each vertex (4 × 90° = 360°)</p>
                    </div>
                    <div className="bg-background p-3 rounded-lg border">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg">⬡</span>
                        <span className="font-semibold text-sm">Regular Hexagons</span>
                      </div>
                      <p className="text-xs text-muted-foreground">3 hexagons meet at each vertex (3 × 120° = 360°)</p>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mt-3">
                    <strong>Why only these three?</strong> At each vertex, the interior angles must sum to exactly 360°. Only these three regular polygons have interior angles that divide evenly into 360°.
                  </p>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="faq-6" className="border-0">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium text-foreground">What's special about regular hexagons in nature?</span>
                </AccordionTrigger>
                <AccordionContent>
                  <p className="text-muted-foreground leading-relaxed mb-3">
                    Regular hexagons appear frequently in nature because they're incredibly efficient! Honeybees build hexagonal cells in their hives for good reason.
                  </p>
                  <div className="space-y-3">
                    <div className="bg-amber-500/10 p-4 rounded-lg border border-amber-500/20">
                      <h4 className="font-semibold text-sm mb-2">🐝 Honeycomb Efficiency</h4>
                      <p className="text-sm text-muted-foreground">
                        Hexagons use the least amount of wax to create cells while maximizing storage space. They tessellate perfectly with no wasted space and are structurally strong.
                      </p>
                    </div>
                    <div className="bg-blue-500/10 p-4 rounded-lg border border-blue-500/20">
                      <h4 className="font-semibold text-sm mb-2">❄️ Snowflake Structure</h4>
                      <p className="text-sm text-muted-foreground">
                        Ice crystals naturally form hexagonal structures due to the molecular arrangement of water molecules, creating the beautiful six-fold symmetry in snowflakes.
                      </p>
                    </div>
                    <div className="bg-green-500/10 p-4 rounded-lg border border-green-500/20">
                      <h4 className="font-semibold text-sm mb-2">🪨 Basalt Columns</h4>
                      <p className="text-sm text-muted-foreground">
                        When lava cools slowly, it contracts and cracks into hexagonal columns (like Giant's Causeway in Ireland). This pattern minimizes the energy needed for cracking.
                      </p>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </Card>
          
          {/* Real-world Applications */}
          <Card className="rounded-2xl p-6 border-2 shadow-lg bg-gradient-to-br from-purple-500/5 to-pink-500/5">
            <h2 className="text-xl font-bold text-foreground mb-5 flex items-center gap-2">
              <Zap className="w-5 h-5 text-purple-600" />
              Real-World Applications
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="p-4 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-2 flex items-center gap-2 text-sm">
                  <span className="text-2xl">🏗️</span>
                  Architecture & Engineering
                </h3>
                <p className="text-xs text-muted-foreground">
                  Pentagon-shaped buildings (like the Pentagon in Virginia), hexagonal tiles, octagonal towers, and polygon-based structural designs for strength and aesthetics.
                </p>
              </div>
              <div className="p-4 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-2 flex items-center gap-2 text-sm">
                  <span className="text-2xl">⚽</span>
                  Sports & Games
                </h3>
                <p className="text-xs text-muted-foreground">
                  Soccer balls (truncated icosahedron), board game tiles, sports field layouts, and dice design using various polygonal shapes.
                </p>
              </div>
              <div className="p-4 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-2 flex items-center gap-2 text-sm">
                  <span className="text-2xl">🔬</span>
                  Science & Nature
                </h3>
                <p className="text-xs text-muted-foreground">
                  Molecular structures, crystal formations, snowflake patterns, honeycomb cells, and basalt column formations all exhibit regular polygon geometry.
                </p>
              </div>
              <div className="p-4 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-2 flex items-center gap-2 text-sm">
                  <span className="text-2xl">🎨</span>
                  Art & Design
                </h3>
                <p className="text-xs text-muted-foreground">
                  Logo design, geometric patterns, Islamic art, quilting patterns, tiling designs, and modern graphic design using polygon principles.
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default PolygonCalculator;
